import {Event} from 'shared/events/Event';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';
import {useAppContext} from 'contexts/ApplicationContext';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {useCheckProductVariations} from 'components/customer/Confirmation/helpers/useCheckProductVariations';
import {usePaymentSettings} from 'components/customer/Confirmation/helpers/usePaymentSettings';
import {useLazyGetJobCostQuery} from 'components/customer/Job/store/jobApi';

const hasPaymentMethod = (settings?: {
    allowPayOnAccount?: boolean;
    enableCardPayments?: boolean;
    enableInvoicePayments?: boolean;
}): boolean =>
    !!(
        settings?.allowPayOnAccount ||
        settings?.enableCardPayments ||
        settings?.enableInvoicePayments
    );

export const useGoToJobConfirmationPage = (): Event => {
    const {userProfile} = useAppContext();
    const navigate = useNavigate();
    const {job} = useJobContext() as PartialJob;
    const {isConfirmationPage} = useIsConfirmationPage();
    const {checkIfThereAreVariations} = useCheckProductVariations();
    const {getPaymentSettings} = usePaymentSettings();
    const [getJobCost] = useLazyGetJobCostQuery();

    const action = useCallback(async (): Promise<boolean> => {
        if (
            isConfirmationPage ||
            !userProfile?.jobConfirmationEnabled ||
            job.status !== JOB_STATUS.PROCESSING
        ) {
            return true;
        }

        const captureDeliveryInfo =
            !userProfile?.capturePickupFreightAtJobCreation;
        if (captureDeliveryInfo) {
            void navigate(`/v2/job/${job.displayId}/confirmation`);
            return false;
        }

        try {
            const [hasVariation, cost, paymentSettings] = await Promise.all([
                checkIfThereAreVariations(),
                getJobCost({jobId: Number(job.id)}).unwrap(),
                getPaymentSettings(20000),
            ]);
            const needsReview =
                hasVariation ||
                !!cost?.defaultFreightApplies ||
                !!cost?.hasIndicativeDeliveryExtras;
            if (cost?.missingRequiredDeliveryExtraGroups?.length) return false;
            if (!needsReview && hasPaymentMethod(paymentSettings)) return true;
        } catch {
            // Keep the existing confirmation step when pricing or payment settings are unavailable.
        }
        void navigate(`/v2/job/${job.displayId}/confirmation`);
        return false;
    }, [
        checkIfThereAreVariations,
        getJobCost,
        getPaymentSettings,
        isConfirmationPage,
        job,
        navigate,
        userProfile?.jobConfirmationEnabled,
        userProfile?.capturePickupFreightAtJobCreation,
    ]);

    return {action};
};
